/**
 * Registration request API — public (request-access) and admin endpoints.
 *
 * Public: POST /api/auth/request-access (no auth)
 * Admin:  GET/POST /api/admin/registration-requests (Bearer admin)
 */

import { apiClient } from '@/services/api';
import apiClientDefault from '@/services/api';

// ── Public types ──────────────────────────────────────────────────────────────

export interface RequestAccessPayload {
  email: string;
  display_name: string;
  requested_role: 'athlete' | 'coach';
  message?: string;
  turnstile_token?: string;
}

// ── Admin types ───────────────────────────────────────────────────────────────

export type RegistrationRequestStatus = 'pending' | 'approved' | 'declined' | 'blocked';

export interface RegistrationRequest {
  id: string;
  email: string;
  display_name: string;
  requested_role: 'athlete' | 'coach';
  message: string | null;
  status: RegistrationRequestStatus;
  created_at: string;
  resolved_at: string | null;
  admin_notes: string | null;
  invitation_id: string | null;
}

export interface RegistrationRequestListResponse {
  items: RegistrationRequest[];
  total: number;
}

export interface ListRegistrationRequestsParams {
  status?: RegistrationRequestStatus | 'all';
  page?: number;
  page_size?: number;
}

// ── Public endpoint ───────────────────────────────────────────────────────────

/**
 * Submit a new access request — no authentication required.
 * Always returns 200 regardless of outcome (enumeration-safe).
 * Throws on 400 (captcha), 422 (validation), 429 (rate limit).
 */
export async function requestAccess(
  payload: RequestAccessPayload
): Promise<{ message: string }> {
  // Use the raw axios instance without the auth interceptor so that the Bearer
  // token (if present for an already-logged-in user visiting this page) is
  // still attached — the backend ignores it for public routes.
  const response = await apiClientDefault.post<{ message: string }>(
    '/auth/request-access',
    payload
  );
  return response.data;
}

// ── Admin read ────────────────────────────────────────────────────────────────

export async function listRegistrationRequests(
  params?: ListRegistrationRequestsParams
): Promise<RegistrationRequestListResponse> {
  const query: Record<string, string | number> = {};
  if (params?.page != null) query.page = params.page;
  if (params?.page_size != null) query.page_size = params.page_size;
  if (params?.status && params.status !== 'all') query.status = params.status;

  const response = await apiClient.get<RegistrationRequestListResponse>(
    '/admin/registration-requests',
    { params: query }
  );
  return response.data;
}

// ── Admin actions ─────────────────────────────────────────────────────────────

export async function approveRegistrationRequest(
  id: string,
  role?: 'athlete' | 'coach'
): Promise<void> {
  await apiClient.post(`/admin/registration-requests/${id}/approve`, role ? { role } : {});
}

export async function declineRegistrationRequest(
  id: string,
  opts?: { admin_notes?: string; notify_requester?: boolean }
): Promise<void> {
  await apiClient.post(`/admin/registration-requests/${id}/decline`, opts ?? {});
}

export async function blockRegistrationRequest(
  id: string,
  reason?: string
): Promise<void> {
  await apiClient.post(`/admin/registration-requests/${id}/block`, reason ? { reason } : {});
}
